import { createScreenText } from 'helpers/screenText';
const st = createScreenText('unsold_ticket_counter_review');

// src/pages/UnsoldTicketCounterReview/types.ts
// US-016 (tiếp nhận & kiểm đếm vé ế vật lý tại quầy) + US-017 (lập phiếu đối soát).
// Field bám đúng DTO thật: TicketService.Contacts/Dtos/UnsoldTicketReturnRequestDto.cs

// MatchStatus / RejectReason dùng chung với phiếu đối soát — khai báo 1 nơi, re-export tại đây
// để component của trang này import gọn.
export {
  MATCH_STATUS,
  MATCH_STATUS_LABELS,
  MATCH_STATUS_COLORS,
  REJECT_REASON,
  REJECT_REASON_LABELS,
  REJECT_REASON_OPTIONS,
} from 'slices/ticketReturnReconciliation/types';
export type { MatchStatus, RejectReason } from 'slices/ticketReturnReconciliation/types';

// Trạng thái yêu cầu hoàn vé ế — int (UnsoldTicketReturnRequestStatuses)
export const RETURN_REQUEST_STATUS = {
  ChoDoiSoat: 1,
  DaDoiSoat: 2,
  DaThamDinh: 3,
  TuChoi: 4,
} as const;

export type ReturnRequestStatus =
  (typeof RETURN_REQUEST_STATUS)[keyof typeof RETURN_REQUEST_STATUS];

// Fallback — DTO đã trả sẵn `statusName` + `statusBadgeColor`
export const RETURN_REQUEST_STATUS_LABELS: Record<string, string> = {
  1: st('cho_doi_soat_text'),
  2: st('da_doi_soat_text'),
  3: st('da_tham_dinh_text'),
  4: st('tu_choi_text'),
};

export const RETURN_REQUEST_STATUS_COLORS: Record<string, string> = {
  1: 'warning',
  2: 'info',
  3: 'success',
  4: 'danger',
};

export const RETURN_REQUEST_STATUS_OPTIONS = [
  { value: 'All', label: st('tat_ca_trang_thai_label') },
  { value: String(RETURN_REQUEST_STATUS.ChoDoiSoat), label: st('cho_doi_soat_text') },
  { value: String(RETURN_REQUEST_STATUS.DaDoiSoat), label: st('da_doi_soat_text') },
  { value: String(RETURN_REQUEST_STATUS.DaThamDinh), label: st('da_tham_dinh_text') },
  { value: String(RETURN_REQUEST_STATUS.TuChoi), label: st('tu_choi_text') },
];

// Cách đại lý khai báo vé trả (TicketReturnMethods) / kiểu từng dòng (TicketReturnScanTypes)
export const RETURN_METHOD_LABELS: Record<string, string> = {
  ScanSingle: st('quet_tung_ve_text'),
  ScanBlock: st('quet_theo_tep_text'),
  NumberRange: st('khai_bao_dai_so_text'),
};

export const SCAN_TYPE_LABELS: Record<string, string> = {
  Single: st('ve_le_text'),
  Block: st('tep_ve_text'),
  Range: st('dai_so_text'),
};

// 1 dòng khai báo của đại lý — UnsoldTicketReturnRequestLineDto
export interface UnsoldTicketReturnRequestLine {
  id: number;
  ticketNumber?: string | null;
  rangeStart?: string | null;
  rangeEnd?: string | null;
  symbol: string;
  scanType: string;
  scannedAt: string;
  // Số vé dòng khai báo này đại diện (1 với Single, RangeEnd-RangeStart+1 với Block/Range)
  ticketCount: number;
}

// Yêu cầu hoàn vé ế — UnsoldTicketReturnRequestDto
export interface UnsoldTicketReturnRequest {
  id: number;
  requestCode: string;

  // BE chỉ trả agentId — tên/mã đại lý map ở FE qua helpers/agentLookup
  agentId: number;

  issuancePlanId?: number | null;
  planCode?: string | null;
  lotteryPeriodId?: number | null;

  returnMethod: string;
  note?: string | null;

  // Tổng số vé đại lý khai báo trả
  totalTicketCount: number;
  // Chưa có nguồn giá vé (README mục 10 — B3) → BE luôn trả 0
  totalAmount: number;

  status: ReturnRequestStatus | number;
  statusName?: string | null;
  statusBadgeColor?: string | null;

  createdAt?: string | null;
  createdBy?: number | null;
  createdByName?: string | null;

  lines: UnsoldTicketReturnRequestLine[];
}

// Kết quả 1 vé vừa quét — TicketReturnScannedLineDto
export interface ScannedLine {
  ticketNumber: string;
  symbol: string;
  matchStatus: number;
  matchStatusName?: string | null;
  rejectReason?: string | null;
  rejectReasonName?: string | null;
}

// Kết quả sau mỗi lần POST scan-count — TicketReturnScanCountResultDto
export interface ScanCountResult {
  requestId: number;
  reconciliationId: number;
  reconciliationCode: string;

  declaredCount: number;
  scannedCount: number;
  validCount: number;
  rejectedCount: number;
  // = declaredCount - scannedCount. Nút "Lập phiếu đối soát" chỉ mở khi = 0.
  remainingCount: number;

  scanned: ScannedLine[];
}

export interface UnsoldTicketReturnFilters {
  agentId: string;
  code: string;
  status: string;
}

export interface UnsoldTicketReturnModalState {
  createReconciliation: boolean;
}

/**
 * Tiến độ hiển thị khi chưa quét lần nào: server chưa trả ScanCountResult, nên suy từ chính
 * yêu cầu (đã khai báo N vé, quét 0 vé).
 */
export const buildInitialProgress = (
  request: UnsoldTicketReturnRequest,
): Pick<
  ScanCountResult,
  'declaredCount' | 'scannedCount' | 'validCount' | 'rejectedCount' | 'remainingCount'
> => ({
  declaredCount: request.totalTicketCount,
  scannedCount: 0,
  validCount: 0,
  rejectedCount: 0,
  remainingCount: request.totalTicketCount,
});

/** Tỷ lệ đã kiểm đếm (%) so với số vé khai báo */
export const getCountedPercent = (progress?: { declaredCount: number; scannedCount: number } | null): number => {
  if (!progress || !progress.declaredCount) return 0;
  const percent = (progress.scannedCount / progress.declaredCount) * 100;
  return Math.min(Math.round(percent * 100) / 100, 100);
};

/**
 * Đã kiểm đếm đủ 100% hay chưa — dùng `remainingCount` do server tính (đúng nguồn sự thật),
 * không tự trừ ở FE.
 */
export const isFullyCounted = (progress?: { remainingCount: number } | null): boolean =>
  !!progress && progress.remainingCount === 0;
